import type { FurniturePart } from "../../types";
import { clamp, part, pill, rect, resetPartIds, seam } from "../geometry";
import { num, type BuildContext, type BuildResult, type FurnitureTemplate } from "./types";

/*
 * Storage: from above you mostly see the top surface plus a thin band on the
 * +y (front) edge marking drawer / door fronts. Seams split that band into
 * the individual drawers or doors so orientation reads at a glance.
 */

export const dresserTemplate: FurnitureTemplate = {
  id: "dresser",
  label: "Dresser",
  frontKind: "drawer",
  defaultMaterial: "wood-dark",
  params: [{ id: "columns", label: "Drawer columns", type: "int", min: 1, max: 4, default: 0 }],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const cols = num(params, "columns", 0) || Math.max(1, Math.round(w / 30));
    const band = clamp(d * 0.09, 1, 1.75);
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: 0.5 }));
    const seams = [];
    for (let i = 1; i < cols; i++) {
      const x = -w / 2 + (w / cols) * i;
      seams.push(seam(x, d / 2 - band, x, d / 2));
    }
    parts.push(part("drawer-face", rect(-w / 2, d / 2 - band, w, band), 0, h + 0.01, { seams }));
    // Handles: one small pill per column, just behind the front band.
    for (let i = 0; i < cols; i++) {
      const cx = -w / 2 + (w / cols) * (i + 0.5);
      parts.push(part("detail", pill(cx, d / 2 - band - 1.1, Math.min(4, w / cols - 4), 0.7), h, h + 0.5));
    }
    return { parts };
  },
};

export const wardrobeTemplate: FurnitureTemplate = {
  id: "wardrobe",
  label: "Wardrobe",
  frontKind: "door",
  defaultMaterial: "wood-dark",
  params: [{ id: "doors", label: "Doors", type: "int", min: 1, max: 4, default: 0 }],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const doors = num(params, "doors", 0) || clamp(Math.round(w / 24), 1, 4);
    const band = clamp(d * 0.07, 1, 1.5);
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: 0.5 }));
    const seams = [];
    for (let i = 1; i < doors; i++) {
      const x = -w / 2 + (w / doors) * i;
      seams.push(seam(x, d / 2 - band, x, d / 2));
    }
    parts.push(part("door-face", rect(-w / 2, d / 2 - band, w, band), 0, h + 0.01, { seams }));
    return { parts };
  },
};

export const bookshelfTemplate: FurnitureTemplate = {
  id: "bookshelf",
  label: "Bookshelf",
  frontKind: "shelf",
  defaultMaterial: "wood-dark",
  params: [{ id: "bays", label: "Bays", type: "int", min: 1, max: 6, default: 0 }],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const bays = num(params, "bays", 0) || Math.max(1, Math.round(w / 30));
    const side = clamp(d * 0.08, 0.75, 1.25);
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h));
    // Open shelf interior: inset from back and sides, open to the front.
    const seams = [];
    for (let i = 1; i < bays; i++) {
      const x = -w / 2 + (w / bays) * i;
      seams.push(seam(x, -d / 2 + side, x, d / 2));
    }
    parts.push(
      part("shelf", rect(-w / 2 + side, -d / 2 + side, w - 2 * side, d - side), 0, h - 0.5, { seams })
    );
    return { parts };
  },
};

export const tvStandTemplate: FurnitureTemplate = {
  id: "tv-stand",
  label: "TV stand",
  frontKind: "screen",
  defaultMaterial: "matte",
  params: [{ id: "tv", label: "Show TV", type: "bool", default: true }],
  build({ w, d, h, params }: BuildContext): BuildResult {
    resetPartIds();
    const showTv = params.tv !== false;
    const parts: FurniturePart[] = [];
    parts.push(part("body", rect(-w / 2, -d / 2, w, d), 0, h, { cornerRadius: 0.75 }));
    if (showTv) {
      // The panel sits toward the back edge and faces +y into the room.
      const tw = Math.min(w - 6, w * 0.82);
      parts.push(
        part("screen", rect(-tw / 2, -d / 2 + 2.5, tw, 1.4), h, h + 24, {
          material: { id: "matte", tint: "#15151a" },
        })
      );
    }
    return { parts };
  },
};
